import React, { useEffect, useState } from 'react';
import { Alert, Spinner } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';

import BottomSheet from 'components/DesignSystem/BottomSheet';
import Section from 'components/Meteorology/Shared/ControlPanelSheet/Section';
import OptionRow from 'components/Meteorology/Shared/ControlPanelSheet/OptionRow';
import SheetActionButton from 'components/Layout/MobileShell/SheetActionButton';
import MedidorComplexidade from 'components/Windx/ControlPanel/MedidorComplexidade';
import useControlPanelRules from 'hooks/Windx/useControlPanelRules';
import useEstimativaWindx from 'helper/windx/useEstimativaWindx';
import { OpcaoRmv, Patamar as PatamarType, TipoRodada } from 'helper/windx';
import {
  selectWindxIdsModelos,
  selectWindxOpcoesRmv,
  selectWindxTipoRodada,
  selectWindxPatamar,
  selectWindxFatorRen,
  selectWindxUnidade,
  selectWindxIdSubdivisaoEspacial,
  selectWindxIdsEntidades,
  selectWindxIdsSimulacoesAntigasAtivas,
  selectWindxFiltrosCompletos,
  setIdsModelos,
  setOpcoesRmv,
  setTipoRodada,
  setPatamar,
  setFatorRen,
  setIdSubdivisaoEspacial,
  resetWindxControlPanel,
  realizarConsulta,
} from 'redux/reducers/windxControlPanelReducer';

import Modelos from './Modelos';
import Preferencias from './Preferencias';
import SimulacoesAntigas from './SimulacoesAntigas';

import './index.scss';

type SheetSection =
  | 'preferencias' | 'estudo' | 'patamar' | 'fatorRen' | 'simulacoes' | '';

const PATAMARES: { id: PatamarType; label: string }[] = [
  { id: 'media', label: 'Média Semanal' },
  { id: 'l', label: 'Leve' },
  { id: 'm', label: 'Médio' },
  { id: 'p', label: 'Pesado' },
];

const RMV_LABELS: Record<OpcaoRmv, string> = {
  com_rmv: 'Com RMV',
  sem_rmv: 'Sem RMV',
};

const TODAS_RMV: OpcaoRmv[] = ['com_rmv', 'sem_rmv'];

/**
 * Painel de Controle do Windx no mobile (spec Windx mobile, D4/D5).
 *
 * Mesma ordem de campos do painel desktop — decisao travada da iniciativa de
 * responsividade (2026-07-08). As regras de o-que-aparece-em-cada-tela vem de
 * `useControlPanelRules`, compartilhado com o desktop, para que as duas telas
 * nao possam divergir.
 *
 * "Realizar consulta" tambem vive aqui, alem da `BarraConsulta` fixa acima
 * da BottomTabBar — mesmo gate do desktop (`filtrosCompletos` e veredito
 * != vermelho), pra nao deixar o usuario preso no painel sem saida.
 *
 * A auto-selecao de RMV nao e replicada aqui de proposito: o efeito que
 * sincroniza `opcoesRmv` contra as disponiveis ja vive em
 * `useControlPanelRules`, entao vale para os dois paineis.
 */
export default function ControlPanelSheet({
  open, dismiss,
}: { open: boolean; dismiss: () => void }) {
  const dispatch = useDispatch();
  const [openSection, setOpenSection] = useState<SheetSection>('');
  const [showSaveForm, setShowSaveForm] = useState(false);

  const {
    opts, isLoading, error, tela,
    exibeRunType, exibeModelosRmv, exibePatamar, exibeFatorRen,
    exibeSimulacoesAntigas, abrirEstudoAutomatico,
    opcoesRmvDisponiveis, modelosVisiveis, idsSemDados, datasRodadaPorId, modoSelecao,
    dataMinima, dataMaxima,
  } = useControlPanelRules();

  // Mesmo default do desktop (decisao do WEOL-SM #99): sem a etapa Tipo de
  // Rodada, "Estudo Automatico" abre aberto. Efeito, e nao so o valor inicial do
  // estado, porque o painel-options pode chegar depois da montagem do sheet.
  useEffect(() => {
    if (abrirEstudoAutomatico) setOpenSection('estudo');
  }, [abrirEstudoAutomatico]);

  const idsModelos = useSelector(selectWindxIdsModelos);
  const opcoesRmv = useSelector(selectWindxOpcoesRmv);
  const tipoRodada = useSelector(selectWindxTipoRodada);
  const patamar = useSelector(selectWindxPatamar);
  const fatorRen = useSelector(selectWindxFatorRen);
  const unidade = useSelector(selectWindxUnidade);
  const idSubdivisao = useSelector(selectWindxIdSubdivisaoEspacial);
  const idsEntidades = useSelector(selectWindxIdsEntidades);
  const idsSimulacoesAntigasAtivas = useSelector(selectWindxIdsSimulacoesAntigasAtivas);
  const filtrosCompletos = useSelector(selectWindxFiltrosCompletos);

  const { estimativa, isFetching: estimativaFetching } = useEstimativaWindx();

  const toggle = (secao: SheetSection) => () => (
    setOpenSection((atual) => (atual === secao ? '' : secao))
  );

  // WINDX-030: minimo 1 RMV marcada — desmarcar a ultima e ignorado.
  const toggleRmv = (opt: OpcaoRmv) => {
    if (opcoesRmv.includes(opt)) {
      if (opcoesRmv.length <= 1) return;
      dispatch(setOpcoesRmv(opcoesRmv.filter((v) => v !== opt)));
      return;
    }
    dispatch(setOpcoesRmv([...opcoesRmv, opt]));
  };

  const corpo = () => {
    if (isLoading) {
      return (
        <div className="p-4 text-center">
          <Spinner animation="border" role="status" size="sm" />
          <span className="ms-2">Carregando opções do painel...</span>
        </div>
      );
    }

    if (error || !opts) {
      return (
        <Alert variant="danger" className="m-3">
          Não foi possível carregar as opções do painel. Tente novamente em instantes.
        </Alert>
      );
    }

    const tipoRodadaRows = exibeRunType && opts.tipos_rodada ? (
      opts.tipos_rodada.map((o) => (
        <OptionRow
          key={o.id}
          label={o.label}
          selected={tipoRodada === o.id}
          onSelect={() => dispatch(setTipoRodada(o.id as TipoRodada))}
        />
      ))
    ) : null;

    // Progressive disclosure (WINDX-061): em qualquer aba com a etapa Tipo de
    // Rodada (SH sempre; Semanal e Comparativo com a flag do BE ligada, WEOL-SM
    // #99), enquanto ele nao foi escolhido. Mesmo texto e testid do desktop.
    const dica = exibeRunType && !tipoRodada ? (
      <p className="windx-sheet-dica" data-testid="windx-dica-tipo-rodada">
        Selecione o Tipo de Rodada para escolher os modelos.
      </p>
    ) : null;

    const modelosCorpo = exibeModelosRmv ? (
      <Modelos
        modelos={modelosVisiveis}
        idsSelecionados={idsModelos}
        onChange={(ids) => dispatch(setIdsModelos(ids))}
        idsIndisponiveis={idsSemDados}
        datasRodadaPorId={datasRodadaPorId}
        tipoRodada={tipoRodada}
        modoSelecao={modoSelecao}
      />
    ) : null;

    const rmvRows = exibeModelosRmv ? TODAS_RMV.map((opt) => (
      <OptionRow
        key={opt}
        label={RMV_LABELS[opt]}
        selected={opcoesRmv.includes(opt)}
        onSelect={() => toggleRmv(opt)}
        selectionType="multiple"
        disabled={opcoesRmvDisponiveis.length > 0 && !opcoesRmvDisponiveis.includes(opt)}
      />
    )) : null;

    return (
      <>
        <Preferencias
          tela={tela}
          open={openSection === 'preferencias'}
          onToggle={toggle('preferencias')}
          showSaveForm={showSaveForm}
          onCloseSaveForm={() => setShowSaveForm(false)}
          snapshot={{
            idsModelos,
            opcoesRmv,
            tipoRodada,
            patamar,
            unidade,
            idSubdivisao,
            idsEntidades,
            idsSimulacoesAntigasAtivas,
            fatorRen,
          }}
        />

        {/* WINDX-035: Tipo de Rodada + Modelos + RMV sob "Estudo Automatico" nas
            tres abas (o Semanal e o Comparativo desde o WEOL-SM #99), como no
            desktop. O layout solto, uma secao para cada, saiu com o #99. */}
        <Section
          label="Estudo Automático"
          open={openSection === 'estudo'}
          onToggle={toggle('estudo')}
        >
          {tipoRodadaRows}
          {dica}
          {modelosCorpo}
          {rmvRows}
        </Section>

        {exibePatamar && (
          <Section label="Patamar" open={openSection === 'patamar'} onToggle={toggle('patamar')}>
            {PATAMARES.map((o) => (
              <OptionRow
                key={o.id}
                label={o.label}
                selected={patamar === o.id}
                onSelect={() => dispatch(setPatamar(o.id))}
              />
            ))}
          </Section>
        )}

        {exibeFatorRen && (
          <Section
            label="Fator da REN"
            open={openSection === 'fatorRen'}
            onToggle={toggle('fatorRen')}
          >
            <OptionRow
              label="Incluir Fator da REN"
              selected={fatorRen}
              onSelect={() => dispatch(setFatorRen(!fatorRen))}
              selectionType="multiple"
            />
          </Section>
        )}

        {exibeSimulacoesAntigas && (
          <Section
            label="Simulações Antigas"
            open={openSection === 'simulacoes'}
            onToggle={toggle('simulacoes')}
          >
            <SimulacoesAntigas opts={opts} dataMinima={dataMinima} dataMaxima={dataMaxima} />
          </Section>
        )}
      </>
    );
  };

  const confirmar = () => {
    dispatch(realizarConsulta());
    dismiss();
  };

  return (
    <BottomSheet
      open={open}
      dismiss={dismiss}
      title="Painel de controle"
      className="windx-control-panel-sheet"
    >
      <div className="windx-control-panel-sheet-body">{corpo()}</div>
      <div className="windx-control-panel-sheet-footer">
        <MedidorComplexidade
          estimativa={estimativa}
          isFetching={estimativaFetching}
          onAgregar={(subdivisao) => dispatch(setIdSubdivisaoEspacial(subdivisao))}
        />
        <div className="windx-control-panel-sheet-footer-secundarios">
          <SheetActionButton variant="clear" onClick={() => dispatch(resetWindxControlPanel())}>
            Limpar
          </SheetActionButton>
          <SheetActionButton variant="secondary" onClick={() => setShowSaveForm(true)}>
            Salvar preferência
          </SheetActionButton>
        </div>
        <SheetActionButton
          variant="confirm"
          onClick={confirmar}
          disabled={!filtrosCompletos || estimativa?.veredito === 'vermelho'}
        >
          Realizar consulta
        </SheetActionButton>
      </div>
    </BottomSheet>
  );
}
